import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React from 'react'
import {
  CATCH_BOARD_PRIORITY,
  getCatchBoardPriorityLabel,
  getCatchBoardReadinessMeta,
} from './catchBoardConstants'
import { getCatchBoardFollowUpDisplay } from 'src/utils/catchBoardFollowUp'
import { getCatchBoardLandedCardDisplay } from 'src/utils/catchBoardMappers'
import { getCatchBoardReadinessChipTip } from 'src/utils/catchBoardTooltips'
import styled from 'styled-components/macro'

/**
 * Presentational Catch Board deal card.
 */
export const CatchBoardCardFace = ({
  deal,
  style,
  innerRef,
  isDragging = false,
  onDealClick,
  now,
  ...dragProps
}) => {
  if (!deal) {
    return null
  }

  const priorityLabel = getCatchBoardPriorityLabel(deal.priority)
  const showPriority =
    deal.priority && deal.priority !== CATCH_BOARD_PRIORITY.MEDIUM
  const readinessMeta = getCatchBoardReadinessMeta(deal.readiness)
  const followUp = getCatchBoardFollowUpDisplay(deal.nextFollowUpAt, now)
  const landedWon = getCatchBoardLandedCardDisplay(deal)
  const lastTouchedLabel = deal.lastTouchedLabel || ''
  const showSignals = Boolean(readinessMeta || followUp)

  return (
    <Card
      ref={innerRef}
      style={style}
      data-testid={`catch-board-card-${deal.dealId}`}
      data-deal-id={deal.dealId}
      data-stage={deal.stage}
      data-dragging={isDragging ? 'true' : 'false'}
      onClick={event => {
        if (isDragging) {
          return
        }
        // Prefer explicit open when not mid-drag (activation distance handles grab vs click).
        onDealClick?.(deal, event)
      }}
      {...dragProps}
    >
      <CardTop>
        <TitleBlock>
          <DealName title={deal.name}>{deal.name || 'Untitled deal'}</DealName>
          {String(deal.companyName || '').trim() ? (
            <CompanyName
              data-testid={`catch-board-card-company-${deal.dealId}`}
              title={String(deal.companyName).trim()}
            >
              {String(deal.companyName).trim()}
            </CompanyName>
          ) : null}
        </TitleBlock>
        {showPriority ? (
          <PriorityBadge data-priority={deal.priority}>
            {priorityLabel}
          </PriorityBadge>
        ) : null}
      </CardTop>

      {showSignals ? (
        <SignalRow>
          {readinessMeta ? (
            <ReadinessBadge
              data-readiness={readinessMeta.key}
              data-testid={`catch-board-card-readiness-${deal.dealId}`}
              title={
                getCatchBoardReadinessChipTip(readinessMeta.key) ||
                readinessMeta.label
              }
            >
              {readinessMeta.cardLabel}
            </ReadinessBadge>
          ) : null}
          {followUp ? (
            <FollowUpBadge
              data-follow-up={followUp.status}
              data-testid={`catch-board-card-follow-up-${deal.dealId}`}
              title={followUp.title}
            >
              {followUp.label}
            </FollowUpBadge>
          ) : null}
        </SignalRow>
      ) : null}

      <ValueBlock>
        <ValueRow>{deal.dealValueLabel || '—'}</ValueRow>
        {Number(deal.potentialDealValuePence) > 0 ? (
          <PotentialRow
            data-testid={`catch-board-card-potential-${deal.dealId}`}
            title={`Potential ${deal.potentialDealValueLabel}`}
          >
            Potential {deal.potentialDealValueLabel}
          </PotentialRow>
        ) : null}
        {landedWon ? (
          <WonBlock
            data-testid={`catch-board-card-won-${deal.dealId}`}
            title={landedWon.title}
          >
            {landedWon.primary ? (
              <WonPrimary>{landedWon.primary}</WonPrimary>
            ) : null}
            {landedWon.goLiveLabel ? (
              <WonSecondary>Go-live {landedWon.goLiveLabel}</WonSecondary>
            ) : null}
          </WonBlock>
        ) : null}
      </ValueBlock>

      {deal.primaryVenueName ? (
        <MetaRow title={deal.primaryVenueName}>{deal.primaryVenueName}</MetaRow>
      ) : (
        <MetaRow muted>No primary venue</MetaRow>
      )}

      <MetaRow muted>
        {deal.ownerName || 'Unassigned'}
        {deal.linkedVenueCount > 1
          ? ` · ${deal.linkedVenueCount} venues`
          : ''}
      </MetaRow>

      {lastTouchedLabel ? (
        <LastTouched
          data-testid={`catch-board-card-last-touched-${deal.dealId}`}
          title={lastTouchedLabel}
        >
          {lastTouchedLabel}
        </LastTouched>
      ) : null}
    </Card>
  )
}

const CatchBoardCard = ({ deal, onDealClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: deal.dealId,
    data: {
      type: 'deal',
      deal,
      stage: deal.stage,
    },
  })

  return (
    <CatchBoardCardFace
      deal={deal}
      innerRef={setNodeRef}
      isDragging={isDragging}
      onDealClick={onDealClick}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.35 : 1,
      }}
      {...attributes}
      {...listeners}
    />
  )
}

const Card = styled.article`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: grab;
  touch-action: none;
  user-select: none;

  &:hover {
    border-color: rgba(0, 0, 0, 0.14);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }

  &:active {
    cursor: grabbing;
  }
`

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

const TitleBlock = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const DealName = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const CompanyName = styled.div`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PriorityBadge = styled.span`
  flex: 0 0 auto;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
  background: ${({ theme }) => theme.colours.lightGrey || '#f0f0f0'};

  &[data-priority='high'] {
    color: #7a3b2e;
    background: #f7ebe7;
  }
`

const SignalRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`

const ReadinessBadge = styled.span`
  flex: 0 0 auto;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;

  &[data-readiness='sales_ready'] {
    color: #2d5a1e;
    background: #e8f3d8;
  }

  &[data-readiness='customer_success'] {
    color: #7a5a1e;
    background: #f7f0e0;
  }

  &[data-readiness='product'] {
    color: #2e4a7a;
    background: #e7eef7;
  }
`

const FollowUpBadge = styled.span`
  flex: 0 0 auto;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
  background: ${({ theme }) => theme.colours.lightGrey || '#f0f0f0'};

  &[data-follow-up='overdue'] {
    color: #7a2e2e;
    background: #f7e7e7;
  }

  &[data-follow-up='due_soon'] {
    color: #7a5a1e;
    background: #f7f0e0;
  }

  &[data-follow-up='upcoming'] {
    color: ${({ theme }) => theme.colours.stormGrey || '#555'};
    background: #eef1f4;
  }
`

const ValueBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`

const ValueRow = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const PotentialRow = styled.div`
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const WonBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 2px;
`

const WonPrimary = styled.div`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  color: #2d5a1e;
`

const WonSecondary = styled.div`
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const MetaRow = styled.div`
  font-size: 12px;
  line-height: 1.35;
  color: ${({ theme, muted }) =>
    muted
      ? theme.colours.stormGrey || '#666'
      : theme.colours.broccoliGreen || '#1a1a1a'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const LastTouched = styled.div`
  margin-top: 2px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 11px;
  line-height: 1.35;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export default CatchBoardCard
